import { StrictMode, Suspense } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { AuthProvider } from './contexts/AuthContext'
import { ToastProvider } from './contexts/ToastContext'
import { RequireAuth } from './components/RequireAuth'
import { ScrollToTop } from './components/ScrollToTop'
import { ConsentManager } from './components/consent/ConsentManager'
import { RequireAdmin, RequireStaff } from './components/RequireAdmin'
import { AnderDoor, RequireAnder } from './components/RequireAnder'
import { Spinner } from './components/ui'
import {
  BOOKING_TERMS_PAGE,
  CONSUMER_HEALTH_DATA_PAGE,
  legalPagePublished,
  PRIVACY_POLICY_PAGE,
  travelerStoriesPublished,
  WEBSITE_TERMS_PAGE,
} from '@wanderlust/shared'
import { reportRecoverableHydrationError } from './lib/hydration'
import { lazyWithReload, STALE_CHUNK_ERROR_PATTERNS } from './lib/lazyWithReload'
import { startConsent } from './lib/consentStore'
import './index.css'

// Before anything renders: a stored privacy choice is put back in force here,
// and nothing measures until it (or, where the opt-in rule does not apply, the
// default) allows it.
startConsent()

// Initialize Sentry for error tracking (only in production with DSN configured)
if (import.meta.env.VITE_SENTRY_DSN && import.meta.env.PROD) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: 0.1, // Sample 10% of transactions
    replaysSessionSampleRate: 0.1, // Sample 10% of sessions for replay
    replaysOnErrorSampleRate: 1.0, // Always capture replay on errors
    ignoreErrors: [
      // Stale chunk errors after deploys — handled by lazyWithReload auto-reload.
      // One list, so what we reload for and what we ignore cannot drift apart.
      ...STALE_CHUNK_ERROR_PATTERNS,
      // Scripts the Instagram/Facebook in-app browsers inject into the page.
      // They probe native bridges that aren't there and throw, and our global
      // handlers report it as ours. None of it is our code.
      /window\.webkit\.messageHandlers/, // iOS WKWebView bridge
      /Java object is gone/, // Android WebView JavascriptInterface bridge
    ],
    denyUrls: [
      // Facebook's Android in-app browser serves its injected scripts from the
      // iabjs: scheme, so anything thrown from there is likewise not ours.
      /^iabjs:/,
    ],
  });
}

// Eagerly loaded (homepage & signin are pre-rendered)
import App from './App.tsx'
import SignIn from './pages/SignIn.tsx'

// Lazy-loaded routes (behind auth or rarely visited)
// Uses lazyWithReload to auto-reload on stale chunk errors after deploys
const SignUp = lazyWithReload(() => import('./pages/SignUp.tsx'))
const ForgotPassword = lazyWithReload(() => import('./pages/ForgotPassword.tsx'))
const ResetPassword = lazyWithReload(() => import('./pages/ResetPassword.tsx'))
const AuthCallback = lazyWithReload(() => import('./pages/AuthCallback.tsx'))
const Trips = lazyWithReload(() => import('./pages/Trips.tsx'))
const TripEditor = lazyWithReload(() => import('./pages/TripEditor.tsx'))
const TripView = lazyWithReload(() => import('./pages/TripView.tsx'))
const AdminHome = lazyWithReload(() => import('./pages/admin/AdminHome.tsx'))
const AdminMyTrips = lazyWithReload(() => import('./pages/AdminMyTrips.tsx'))
const AdminInvite = lazyWithReload(() => import('./pages/AdminInvite.tsx'))
const AdminUserLookup = lazyWithReload(() => import('./pages/AdminUserLookup.tsx'))
const AdminClients = lazyWithReload(() => import('./pages/admin/Clients.tsx'))
const AdminClientNew = lazyWithReload(() => import('./pages/admin/ClientNew.tsx'))
const AdminDestinations = lazyWithReload(() => import('./pages/admin/Destinations.tsx'))
const AdminTripTemplates = lazyWithReload(() => import('./pages/admin/TripTemplates.tsx'))
const AdminTripTemplateDetail = lazyWithReload(() => import('./pages/admin/TripTemplateDetail.tsx'))
const AdminDestinationDetail = lazyWithReload(() => import('./pages/admin/DestinationDetail.tsx'))
const AdminSuppliers = lazyWithReload(() => import('./pages/admin/Suppliers.tsx'))
const AdminSupplierOrganization = lazyWithReload(() => import('./pages/admin/SupplierOrganizationDetail.tsx'))
const AdminSupplierContact = lazyWithReload(() => import('./pages/admin/SupplierContactDetail.tsx'))
const AdminSupplierCapture = lazyWithReload(() => import('./pages/admin/SupplierCapture.tsx'))
const AdminSupplierCaptureReview = lazyWithReload(() => import('./pages/admin/SupplierCaptureReview.tsx'))
const AdminSupplierFollowUps = lazyWithReload(() => import('./pages/admin/SupplierFollowUps.tsx'))
const AdminTasks = lazyWithReload(() => import('./pages/admin/Tasks.tsx'))
const AdminSocialHome = lazyWithReload(() => import('./pages/admin/SocialHome.tsx'))
const AdminSocialSession = lazyWithReload(() => import('./pages/admin/SocialSession.tsx'))
const AdminSocialTemplate = lazyWithReload(() => import('./pages/admin/SocialTemplate.tsx'))
const ItineraryBuilder = lazyWithReload(() => import('./pages/admin/ItineraryBuilder.tsx'))
const Blog = lazyWithReload(() => import('./pages/Blog.tsx'))
const BlogPostPage = lazyWithReload(() => import('./pages/BlogPost.tsx'))
const NewsletterStatus = lazyWithReload(() => import('./pages/NewsletterStatus.tsx'))
const BlogList = lazyWithReload(() => import('./pages/admin/BlogList.tsx'))
const BlogEditor = lazyWithReload(() => import('./pages/admin/BlogEditor.tsx'))
const BlogCategories = lazyWithReload(() => import('./pages/admin/BlogCategories.tsx'))
const BlogTags = lazyWithReload(() => import('./pages/admin/BlogTags.tsx'))
const BlogAuthors = lazyWithReload(() => import('./pages/admin/BlogAuthors.tsx'))
const AdminAnalytics = lazyWithReload(() => import('./pages/admin/Analytics.tsx'))
const TripRequest = lazyWithReload(() => import('./pages/TripRequest.tsx'))
const LeadIntake = lazyWithReload(() => import('./pages/LeadIntake.tsx'))
const AdminLeads = lazyWithReload(() => import('./pages/admin/Leads.tsx'))
const AdminDreams = lazyWithReload(() => import('./pages/admin/Dreams.tsx'))
const AdminLeadDetail = lazyWithReload(() => import('./pages/admin/LeadDetail.tsx'))
const AdminCommunications = lazyWithReload(() => import('./pages/admin/Communications.tsx'))
const Brand = lazyWithReload(() => import('./pages/Brand.tsx'))
const BrandAnder = lazyWithReload(() => import('./pages/BrandAnder.tsx'))
const BrandWanderlust = lazyWithReload(() => import('./pages/BrandWanderlust.tsx'))
const FgesTurkeyTrot = lazyWithReload(() => import('./pages/FgesTurkeyTrot.tsx'))
const TravelerStories = lazyWithReload(() => import('./pages/TravelerStories.tsx'))
const PrivacyPolicy = lazyWithReload(() => import('./pages/PrivacyPolicy.tsx'))
const WebsiteTerms = lazyWithReload(() => import('./pages/WebsiteTerms.tsx'))
const BookingTerms = lazyWithReload(() => import('./pages/BookingTerms.tsx'))
const ConsumerHealthDataPrivacy = lazyWithReload(() => import('./pages/ConsumerHealthDataPrivacy.tsx'))
const AnderStart = lazyWithReload(() => import('./pages/AnderStart.tsx'))
const AnderClaim = lazyWithReload(() => import('./pages/AnderClaim.tsx'))
const AnderThreads = lazyWithReload(() => import('./pages/AnderThreads.tsx'))
const AnderThreadPage = lazyWithReload(() => import('./pages/AnderThread.tsx'))
const TripWorkspacePage = lazyWithReload(() => import('./pages/TripWorkspace.tsx'))
const TripOptionsPage = lazyWithReload(() => import('./pages/TripOptions.tsx'))
const TripBookingsPage = lazyWithReload(() => import('./pages/TripBookings.tsx'))
const TripDocumentsPage = lazyWithReload(() => import('./pages/TripDocuments.tsx'))
const TripConversationPage = lazyWithReload(() => import('./pages/TripConversation.tsx'))
const TripFeedbackPage = lazyWithReload(() => import('./pages/TripFeedback.tsx'))
const HelpPage = lazyWithReload(() => import('./pages/Help.tsx'))
const MembershipPage = lazyWithReload(() => import('./pages/Membership.tsx'))
const ProfilePage = lazyWithReload(() => import('./pages/Profile.tsx'))
const NotificationsPage = lazyWithReload(() => import('./pages/Notifications.tsx'))
const AdminAnderQueue = lazyWithReload(() => import('./pages/admin/AnderQueue.tsx'))
const AdminAnderThreads = lazyWithReload(() => import('./pages/admin/AnderThreads.tsx'))
const AdminAnderThread = lazyWithReload(() => import('./pages/admin/AnderThreadAdmin.tsx'))
const AdminAnderSupport = lazyWithReload(() => import('./pages/admin/AnderSupport.tsx'))
const AdminAnderSettings = lazyWithReload(() => import('./pages/admin/AnderSettings.tsx'))

const container = document.getElementById('root')!;
const app = (
  <StrictMode>
    <AuthProvider>
      <ToastProvider>
        <BrowserRouter>
          <ScrollToTop />
          <ConsentManager />
          <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Spinner size="lg" /></div>}>
            <Routes>
              <Route path="/" element={<App />} />
              <Route path="/signin" element={<SignIn />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/trips" element={<RequireAuth><Trips /></RequireAuth>} />
              <Route path="/trips/request" element={<RequireAuth><TripRequest /></RequireAuth>} />
              <Route path="/trips/new" element={<RequireAuth><TripEditor /></RequireAuth>} />
              <Route path="/trips/:tripId" element={<RequireAuth><TripView /></RequireAuth>} />
              <Route path="/trips/:tripId/edit" element={<RequireAuth><TripEditor /></RequireAuth>} />
              <Route path="/admin" element={<RequireStaff><AdminHome /></RequireStaff>} />
              <Route path="/admin/my-trips" element={<RequireAdmin><AdminMyTrips /></RequireAdmin>} />
              <Route path="/admin/invite" element={<RequireAdmin><AdminInvite /></RequireAdmin>} />
              <Route path="/admin/user-lookup" element={<RequireAdmin><AdminUserLookup /></RequireAdmin>} />
              <Route path="/admin/clients" element={<RequireAdmin><AdminClients /></RequireAdmin>} />
              <Route path="/admin/clients/new" element={<RequireAdmin><AdminClientNew /></RequireAdmin>} />
              <Route path="/admin/clients/:clientId" element={<RequireAdmin><AdminClients /></RequireAdmin>} />
              <Route path="/admin/destinations" element={<RequireStaff><AdminDestinations /></RequireStaff>} />
              <Route path="/admin/destinations/:destinationId" element={<RequireStaff><AdminDestinationDetail /></RequireStaff>} />
              {/* Suppliers are admin-only: the records carry commercial terms
                  and personal contact details for the trade. */}
              <Route path="/admin/suppliers" element={<RequireAdmin><AdminSuppliers /></RequireAdmin>} />
              <Route path="/admin/suppliers/capture" element={<RequireAdmin><AdminSupplierCapture /></RequireAdmin>} />
              <Route path="/admin/suppliers/captures/:captureId" element={<RequireAdmin><AdminSupplierCaptureReview /></RequireAdmin>} />
              <Route path="/admin/suppliers/follow-ups" element={<RequireAdmin><AdminSupplierFollowUps /></RequireAdmin>} />
              <Route path="/admin/tasks" element={<RequireAdmin><AdminTasks /></RequireAdmin>} />
              <Route path="/admin/suppliers/organizations/:organizationId" element={<RequireAdmin><AdminSupplierOrganization /></RequireAdmin>} />
              <Route path="/admin/suppliers/contacts/:contactId" element={<RequireAdmin><AdminSupplierContact /></RequireAdmin>} />
              <Route path="/admin/trip-templates" element={<RequireAdmin><AdminTripTemplates /></RequireAdmin>} />
              <Route path="/admin/trip-templates/:templateId" element={<RequireAdmin><AdminTripTemplateDetail /></RequireAdmin>} />
              <Route path="/admin/social" element={<RequireStaff><AdminSocialHome /></RequireStaff>} />
              <Route path="/admin/social/sessions/:sessionId" element={<RequireStaff><AdminSocialSession /></RequireStaff>} />
              <Route path="/admin/social/templates/:templateId" element={<RequireStaff><AdminSocialTemplate /></RequireStaff>} />
              <Route path="/admin/trips/new" element={<RequireAdmin><TripEditor /></RequireAdmin>} />
              <Route path="/admin/trips/:tripId" element={<RequireAdmin><TripView /></RequireAdmin>} />
              <Route path="/admin/trips/:tripId/edit" element={<RequireAdmin><TripEditor /></RequireAdmin>} />
              <Route path="/admin/trips/:tripId/builder" element={<RequireAdmin><ItineraryBuilder /></RequireAdmin>} />
              <Route path="/plan" element={<LeadIntake />} />
              {/* Ander, by Wanderlust Cravings: the preview before an account, the signed-in
                  conversations and the rest of the portal. Not announced yet, so
                  only an admin is let in (lib/anderAccess.ts). */}
              <Route path="/ander" element={<AnderDoor><AnderStart /></AnderDoor>} />
              <Route path="/ander/claim" element={<RequireAnder><AnderClaim /></RequireAnder>} />
              <Route path="/ander/threads" element={<RequireAnder><AnderThreads /></RequireAnder>} />
              <Route path="/ander/t/:threadId" element={<RequireAnder><AnderThreadPage /></RequireAnder>} />
              <Route path="/trips/:tripId/plan" element={<RequireAnder><TripWorkspacePage /></RequireAnder>} />
              <Route path="/trips/:tripId/options" element={<RequireAnder><TripOptionsPage /></RequireAnder>} />
              <Route path="/trips/:tripId/bookings" element={<RequireAnder><TripBookingsPage /></RequireAnder>} />
              <Route path="/trips/:tripId/documents" element={<RequireAnder><TripDocumentsPage /></RequireAnder>} />
              <Route path="/trips/:tripId/conversation" element={<RequireAnder><TripConversationPage /></RequireAnder>} />
              <Route path="/trips/:tripId/feedback" element={<RequireAnder><TripFeedbackPage /></RequireAnder>} />
              <Route path="/help" element={<RequireAnder><HelpPage /></RequireAnder>} />
              <Route path="/membership" element={<RequireAnder><MembershipPage /></RequireAnder>} />
              <Route path="/profile" element={<RequireAnder><ProfilePage /></RequireAnder>} />
              <Route path="/notifications" element={<RequireAnder><NotificationsPage /></RequireAnder>} />
              <Route path="/admin/ander" element={<RequireAdmin><AdminAnderQueue /></RequireAdmin>} />
              <Route path="/admin/ander/threads" element={<RequireAdmin><AdminAnderThreads /></RequireAdmin>} />
              <Route path="/admin/ander/threads/:threadId" element={<RequireAdmin><AdminAnderThread /></RequireAdmin>} />
              <Route path="/admin/ander/support" element={<RequireAdmin><AdminAnderSupport /></RequireAdmin>} />
              <Route path="/admin/ander/support/:requestId" element={<RequireAdmin><AdminAnderSupport /></RequireAdmin>} />
              <Route path="/admin/ander/settings" element={<RequireAdmin><AdminAnderSettings /></RequireAdmin>} />
              <Route path="/blog" element={<Blog />} />
              {/*
                /card (+ /contact, /bizcard, /contactcard) is intentionally NOT a
                React route. It's a standalone static page generated from the theme
                tokens by build-card.mjs and served directly (dev: vite plugin in
                vite.config.ts; prod: dist/<alias>/index.html via the API's
                not-found handler) so it loads with minimal traffic on bad networks.
              */}
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/newsletter" element={<NewsletterStatus />} />
              {/* Counsel's legal pages, each linked from every public footer and
                  prerendered. The privacy policy is also where the consent
                  banner and the privacy settings send people. The consumer
                  health policy is unregistered until it has a publication
                  date (CONSUMER_HEALTH_DATA_POLICY_PUBLISHED_ON in shared),
                  which turns on its route, footer link, prerender and sitemap
                  entry together. */}
              <Route path={PRIVACY_POLICY_PAGE.path} element={<PrivacyPolicy />} />
              <Route path={WEBSITE_TERMS_PAGE.path} element={<WebsiteTerms />} />
              <Route path={BOOKING_TERMS_PAGE.path} element={<BookingTerms />} />
              {legalPagePublished(CONSUMER_HEALTH_DATA_PAGE.key) && (
                <Route path={CONSUMER_HEALTH_DATA_PAGE.path} element={<ConsumerHealthDataPrivacy />} />
              )}
              {/* Wanderlust Cravings' own home for what travelers say, so the
                  proof the planning form points at survives a change of host
                  agency. Prerendered: it is a page people are sent to before
                  they trust the site enough to wait for JavaScript.

                  Unregistered until there are approved testimonials to put on
                  it, so the URL behaves like any other address nobody has
                  built yet rather than resolving to a page that looks broken.
                  Adding two entries to the shared travelerProof config turns
                  the route, the homepage band, the planning form's link and
                  the sitemap entry on together. */}
              {travelerStoriesPublished() && (
                <Route path="/traveler-stories" element={<TravelerStories />} />
              )}
              {/* Sponsor landing page for the Fountain Green Elementary PTA's
                  Turkey Trot. Short because it goes on a banner and gets typed
                  from memory. React Router matches it case-insensitively, so
                  `/FGES` lands here too. */}
              <Route path="/fges" element={<FgesTurkeyTrot />} />
              {/* The URL this page was first built at, before it was shortened.
                  Kept as a redirect in case it reached anyone. */}
              <Route path="/FGESTurkeyTrot2026" element={<Navigate to="/fges" replace />} />
              <Route path="/admin/leads" element={<RequireAdmin><AdminLeads /></RequireAdmin>} />
              <Route path="/admin/dreams" element={<RequireAdmin><AdminDreams /></RequireAdmin>} />
              <Route path="/admin/leads/:id" element={<RequireAdmin><AdminLeadDetail /></RequireAdmin>} />
              {/* One route for the inbox and an open conversation, so the list stays put while conversations change beside it. */}
              <Route path="/admin/communications/:id?" element={<RequireAdmin><AdminCommunications /></RequireAdmin>} />
              <Route path="/admin/analytics" element={<RequireAdmin><AdminAnalytics /></RequireAdmin>} />
              <Route path="/admin/blog" element={<RequireStaff><BlogList /></RequireStaff>} />
              <Route path="/admin/blog/new" element={<RequireStaff><BlogEditor /></RequireStaff>} />
              <Route path="/admin/blog/:postId/edit" element={<RequireStaff><BlogEditor /></RequireStaff>} />
              <Route path="/admin/blog/categories" element={<RequireStaff><BlogCategories /></RequireStaff>} />
              <Route path="/admin/blog/tags" element={<RequireStaff><BlogTags /></RequireStaff>} />
              <Route path="/admin/blog/authors" element={<RequireStaff><BlogAuthors /></RequireStaff>} />
              <Route path="/brand" element={<RequireAuth><Brand /></RequireAuth>} />
              <Route path="/brand/ander" element={<RequireAuth><BrandAnder /></RequireAuth>} />
              <Route path="/brand/wanderlust" element={<RequireAuth><BrandWanderlust /></RequireAuth>} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </ToastProvider>
    </AuthProvider>
  </StrictMode>
);

if (container.hasChildNodes()) {
  hydrateRoot(container, app, {
    onRecoverableError: reportRecoverableHydrationError,
  });
} else {
  createRoot(container).render(app);
}
